import { classList } from '../../../utils/string.ts';

type NegationOptions = {
  /**
   * Negates the modal's according padding.
   */
  negate: boolean;
  /**
   * Transfers the negated paddings to this element instead.
   * Always defaults to true, as this is most commonly wanted.
   */
  transfer?: boolean;
};

export type NegateModalPaddingOptions = {
  /**
   * Negates the modal's left and right padding.
   * This is the most common use case, so it defaults to true.
   */
  horizontal: NegationOptions;
  /**
   * Negates the modal's bottom padding. Defaults to false.
   */
  bottom: NegationOptions;
};

/**
 * Modals have standardized paddings. But sometimes elements need to overlap those paddings, so that they
 * visually look better (e.g. horizontal separation lines or elements with a full width background color).
 *
 * For these cases, this function returns classes that apply negative margins.
 * Negative margins should be used cautiously, but in this case, they have advantages over other solutions.
 */
export const negateModalPaddingClasses = (
  options: NegateModalPaddingOptions = { horizontal: { negate: true }, bottom: { negate: false } }
) => {
  const horizontal: NegationOptions = {
    negate: options.horizontal?.negate ?? true,
    transfer: options.horizontal?.transfer ?? true
  };
  const bottom: NegationOptions = {
    negate: !!options.bottom?.negate,
    transfer: options.bottom?.transfer ?? true
  };

  return classList(
    horizontal.negate && '-ml-4 -mr-4',
    bottom.negate && '-mb-4',
    horizontal.transfer && horizontal.negate && 'pl-4 pr-4',
    bottom.transfer && bottom.negate && 'pb-4'
  );
};
